<template>
  <div class="modern-dtr-page">
    <CCard class="shadow-sm border-0 page-card w-100">
          <CCardHeader class="page-header">
            <div class="d-flex align-items-center">
              <span class="header-icon">
                <CIcon name="cil-justify-center" />
              </span>
              <div>
                <strong>Daily Time Records</strong>
                <div class="header-subtitle">Upload attendance logs and review employee DTR records.</div>
              </div>
            </div>
            <div class="card-header-actions">
              <a
                href="#"
                class="card-header-action"
                rel="noreferrer noopener"
                target="_blank"
              >
                <small class="text-muted">DTR</small>
              </a>
            </div>
          </CCardHeader>
          <CCardBody>
            <div class="d-flex justify-content-start mb-3">
              <div
                v-if="refreshLoader"
                class="spinner-border spinner-border-sm"
                role="status"
              >
                <span class="sr-only">Loading...</span>
              </div>
            </div>
            <form @submit.prevent="storeCSVtoDatabase" class="upload-panel">
              <div class="row align-items-center g-2">
                <!-- COMPONENT START -->
                <div class="custom-file col-12 col-md-10">
                  <label
                    class="custom-file-label"
                    id="nameFile1"
                    for="customFile1"
                    >Select File</label
                  >
                  <input
                    type="file"
                    accept="image/*"
                    class="custom-file-input"
                    ref="file_attendance"
                    @change="uploadCSV"
                    id="attendance"
                    name="attendance"
                  />
                </div>
                <!-- COMPONENT END -->
                <div class="form-group col-12 col-md-2 mb-0">
                  <button
                    type="submit"
                    class="btn btn-primary upload-btn mr-1"
                    v-if="!loading"
                  >
                    Upload
                  </button>
                  <button
                    class="btn btn-primary upload-btn"
                    type="button"
                    disabled
                    v-if="loading"
                  >
                    <span
                      class="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Uploading...
                  </button>
                </div>
              </div>
            </form>

            <div class="period-panel mb-4">
              <div class="row align-items-end g-2">
                <div class="col-12 col-md-3">
                  <label class="period-label">Period from</label>
                  <input v-model="periodFrom" type="date" class="form-control" />
                </div>
                <div class="col-12 col-md-3">
                  <label class="period-label">Period to</label>
                  <input v-model="periodTo" type="date" class="form-control" />
                </div>
                <div class="col-12 col-md-6 d-flex flex-wrap align-items-center">
                  <button
                    type="button"
                    class="btn btn-outline-primary btn-sm mr-2 mb-2"
                    :disabled="employeesLoading"
                    @click="loadEmployees"
                  >
                    <span
                      v-if="employeesLoading"
                      class="spinner-border spinner-border-sm mr-1"
                    ></span>
                    Refresh lists
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary btn-sm mr-2 mb-2"
                    @click="openBatchPrint('Regular')"
                  >
                    <i class="bi bi-printer mr-1"></i>
                    Print all regular
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary btn-sm mb-2"
                    @click="openBatchPrint('COS')"
                  >
                    <i class="bi bi-printer mr-1"></i>
                    Print all COS
                  </button>
                </div>
              </div>
              <small class="text-muted d-block mt-2">
                Set the period once here. View and print icons on each row use these dates.
                Lists include active CHED employees with a biometric ID — plantilla in
                Regular, non-plantilla in Contract of Services. Inactive accounts are excluded.
              </small>
            </div>

            <div class="row employee-lists-row">
              <div class="col-12 col-lg-6 mb-4 mb-lg-0">
                <CTableRegular
                  :items="regularEmployees"
                  :period-from="periodFrom"
                  :period-to="periodTo"
                >
                  <template #header>
                    <CIcon name="cil-grid" /> Regular Employees
                    <div class="card-header-actions">
                      <a
                        href="https://coreui.io/vue/docs/components/nav"
                        class="card-header-action"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        <small class="text-muted">list</small>
                      </a>
                    </div>
                  </template>
                </CTableRegular>
              </div>
              <div class="col-12 col-lg-6">
                <CTableCOS
                  :items="cosEmployees"
                  :period-from="periodFrom"
                  :period-to="periodTo"
                >
                  <template #header>
                    <CIcon name="cil-grid" /> Contract of Services
                    <div class="card-header-actions">
                      <a
                        href="https://coreui.io/vue/docs/components/nav"
                        class="card-header-action"
                        rel="noreferrer noopener"
                        target="_blank"
                      >
                        <small class="text-muted">list</small>
                      </a>
                    </div>
                  </template>
                </CTableCOS>
              </div>
            </div>
          </CCardBody>
        </CCard>
  </div>
</template>
<script>
import CTableRegular from './components/RegularTable.vue';
import CTableCOS from './components/COSTable.vue';
import Papa from 'papaparse';

function formatDateInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function currentMonthPeriod() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    from: formatDateInput(from),
    to: formatDateInput(to),
  };
}

export default {
  name: 'DTR',
  components: { CTableRegular, CTableCOS },
  data() {
    const period = currentMonthPeriod();
    return {
      loading: false,
      attendance_logs: [],
      regularEmployees: [],
      cosEmployees: [],
      employeesLoading: false,
      periodFrom: period.from,
      periodTo: period.to,
      refreshLoader: false,
    };
  },
  mounted() {
    this.removeStalePrintStyles();
    this.loadEmployees();
  },
  methods: {
    removeStalePrintStyles() {
      const stale = document.getElementById("dtr-bulk-print-styles");
      if (stale) {
        stale.remove();
      }
      document.body.classList.remove("bulk-dtr-print-active");
    },
    loadEmployees() {
      this.employeesLoading = true;

      Promise.all([
        axios.get('api/dtr-employees', { params: { role: 'Regular' } }),
        axios.get('api/dtr-employees', { params: { role: 'COS' } }),
      ])
        .then(([regularRes, cosRes]) => {
          this.regularEmployees = regularRes.data || [];
          this.cosEmployees = cosRes.data || [];
        })
        .catch(() => {
          toastr.error('Failed to load employee lists.', 'Error');
        })
        .finally(() => {
          this.employeesLoading = false;
        });
    },
    openBatchPrint(type) {
      if (!this.periodFrom || !this.periodTo) {
        toastr.error('Select a period from and to date first.', 'Error');
        return;
      }

      this.$router.push({
        name: 'dtrBatchPrint',
        query: {
          type,
          from: this.periodFrom,
          to: this.periodTo,
        },
      });
    },
    uploadCSV(event) {
      let _this = this;

      var csv = event.target.files[0];
      var file = $('#attendance').val();
      var ext = ['dat'];

      if (file) {
        var get_ext = file.split('.');
        get_ext = get_ext.reverse();

        if ($.inArray(get_ext[0].toLowerCase(), ext) > -1) {
          const fileName = event.target.files[0].name;
          $('.custom-file-label').html(fileName);

          Papa.parse(csv, {
            complete: function(results) {
              _this.attendance_logs = results.data.map(row => {
                // Format the date
                const dateStr = row[1];
                const date = new Date(dateStr);
                const formattedDate = date.toLocaleString('en-US', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false
                }).replace(/(\d+)\/(\d+)\/(\d+)/, '$3-$1-$2').replace(',', '');

                // Transform In/Out status
                const inOutStatus = row[3] === '0' ? 'Check-In' : 'Check-Out';

                return {
                  "Personnel ID": row[0].trim(),
                  "Date And Time": formattedDate,
                  "In/Out Status": inOutStatus
                };
              });
              
              console.log('attendance logs', _this.attendance_logs);
            },
            skipEmptyLines: true,
            header: false,
            transformHeader: function(header) {
              return header.trim();
            }
          });
        } else {
          toastr.warning('Invalid file! Please upload DAT file.');
        }
      }
    },
    storeCSVtoDatabase() {
      this.loading = true;
      axios
        .post('api/upload-dtr', {
          data: { attendances: this.attendance_logs },
        })
        .then((response) => {
          toastr.success(response.data.message);
          this.loading = false;
          $('.custom-file-label').html('');
        })
        .catch(() => {
          alert('Something went wrong!');
        });
    },
  },
};
</script>

<style scoped>
.bi-arrow-clockwise {
  font-size: 1rem;
}

.modern-dtr-page {
  width: 100%;
  max-width: 100%;
}

.modern-dtr-page .page-card {
  border-radius: 12px;
  overflow: hidden;
  width: 100%;
}

.modern-dtr-page .card-body {
  width: 100%;
}

.employee-lists-row {
  margin-left: 0;
  margin-right: 0;
}

.employee-lists-row > [class*="col-"] {
  padding-left: 0;
  padding-right: 0;
}

@media (min-width: 992px) {
  .employee-lists-row > [class*="col-"]:first-child {
    padding-right: 0.75rem;
  }

  .employee-lists-row > [class*="col-"]:last-child {
    padding-left: 0.75rem;
  }
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 21, 0.06);
  padding: 1rem 1.25rem;
}

.header-icon {
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.8rem;
  border-radius: 12px;
  color: #4d8dff;
  background: rgba(77, 141, 255, 0.12);
  flex-shrink: 0;
}

.header-subtitle {
  margin-top: 0.15rem;
  font-size: 0.82rem;
  font-weight: 400;
  color: #768192;
}

.upload-panel .row,
.period-panel .row,
.employee-lists-row {
  width: 100%;
  max-width: 100%;
}

.upload-panel {
  padding: 1rem;
  margin-bottom: 1.25rem;
  background: linear-gradient(120deg, rgba(77, 141, 255, 0.08), rgba(77, 141, 255, 0.02));
  border: 1px solid rgba(77, 141, 255, 0.12);
  border-radius: 12px;
}

.upload-panel .row {
  margin-left: 0;
  margin-right: 0;
}

.custom-file-label {
  border-radius: 10px;
  border-color: rgba(0, 0, 21, 0.1);
}

.custom-file-label::after {
  border-radius: 0 10px 10px 0;
  background: #f6f9ff;
  color: #4d8dff;
  border-left-color: rgba(0, 0, 21, 0.08);
}

.upload-btn {
  border-radius: 10px;
  min-height: 38px;
}

.period-panel {
  padding: 1rem 1.1rem;
  background: #fff;
  border: 1px solid rgba(0, 0, 21, 0.08);
  border-radius: 12px;
}

.period-label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: #768192;
  margin-bottom: 0.35rem;
}

.period-panel .form-control {
  border-radius: 10px;
  border-color: rgba(0, 0, 21, 0.1);
  min-height: 38px;
}
</style>
